import React, {
  CSSProperties,
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef
} from 'react'

import { useQueryClient } from '@tanstack/react-query'
import SelectionArea from '@viselect/react'
import cn from 'classnames'
import { useFilesList } from 'modules/desktop/modules/files-integration/api'
import { FileDropzone } from 'modules/desktop/modules/files-integration/components/FileDropzone'
import { FilePreviewModal } from 'modules/desktop/modules/files-integration/components/FilePreviewModal'
import { FileUploadProgressToast } from 'modules/desktop/modules/files-integration/components/FileUploadProgressToast'
import { NewFolderPlaceholder } from 'modules/desktop/modules/files-integration/components/NewFolderPlaceholder'
import { useProvisionDefaultShortcuts } from 'modules/desktop/modules/files-integration/hooks/useProvisionDefaultShortcuts'
import { useProvisionMyBillsShortcut } from 'modules/desktop/modules/files-integration/hooks/useProvisionMyBillsShortcut'
import { useProvisionNeedHelpShortcut } from 'modules/desktop/modules/files-integration/hooks/useProvisionNeedHelpShortcut'
import { useProvisionNewGrantApplicationShortcut } from 'modules/desktop/modules/files-integration/hooks/useProvisionNewGrantApplicationShortcut'
import { getShortcutGroupCapabilities } from 'modules/desktop/shortcutGroups'
import { UserContext } from 'shared/contexts/User'
import { ShortcutGridItem } from 'shared/types/desktop-grid'

import { GridViewItem } from '../../../../modules/files-integration/components/GridViewItem'
import {
  useDesktopStore,
  useSelectionStore,
  useShortcutGroupStore
} from '../../stores'
import { useDismissDesktopContextMenus } from './DesktopContextMenuContext'
import styles from './DesktopGrid.module.scss'
import { DesktopGridProps } from './DesktopGrid.types'
import {
  DesktopGridWrapper,
  DropPreviewItem,
  ShortcutGroup
} from './components'
import groupCardStyles from './components/ShortcutGroup/ShortcutGroup.module.scss'
import {
  useBaseLayout,
  useDesktopBroadcastListener,
  useDesktopCrossAppBridge,
  useDesktopItemsPersistence,
  useDragAndDrop,
  useFilesListSync,
  useGridCalculation,
  useGridResizeReflow,
  useMarqueeSelection,
  useMoveFilesItemsIntoFolder,
  useMoveFilesItemsToDesktop,
  useMoveItemsIntoFolder,
  useProvisionOfficeToolsGroup,
  useShortcutGroupsPersistence
} from './hooks'
import {
  GRID_ITEM_SELECTOR,
  getShortcutGroupCollisionItems,
  getShortcutGroupInsertionPlan,
  getShortcutGroupPreviewBounds,
  getShortcutGroupTitleBarHeight
} from './utils'
import { getItemBorderRadius } from './utils/getItemBorderRadius'

const DesktopGridContent: FC<DesktopGridProps> = () => {
  const queryClient = useQueryClient()
  const { user } = useContext(UserContext)
  const clearSelection = useSelectionStore(state => state.clear)

  const items = useDesktopStore(state => state.items)
  const setItems = useDesktopStore(state => state.setItems)
  const shortcutGroups = useShortcutGroupStore(state => state.groups)
  const addMembersToShortcutGroup = useShortcutGroupStore(
    state => state.addMembers
  )
  const removeShortcutGroupMembers = useShortcutGroupStore(
    state => state.removeMembers
  )
  const itemsRef = useRef(items)
  itemsRef.current = items
  const pinnedLayoutKey = items
    .filter(item => item.isPinned)
    .map(
      item =>
        `${item.id}:${item.position.row},${item.position.col}:${item.size.width}x${item.size.height}`
    )
    .join('|')

  const refreshFilesList = useCallback(async () => {
    const queryKey = ['files-list'] as const
    await queryClient.invalidateQueries({ queryKey })
    await queryClient.refetchQueries({ queryKey })
  }, [queryClient])

  const {
    containerRef: gridRef,
    cols,
    rows,
    fitCols,
    fitRows,
    cellSize,
    offset,
    setRequiredCols,
    setRequiredRows
  } = useGridCalculation()

  const commitManualItems = useBaseLayout({ items, cols, rows })

  const syncItemsWithFilesList = useFilesListSync({
    cols,
    rows,
    commitManualItems
  })

  const { data: filesList } = useFilesList({
    enabled: cols > 0 && rows > 0,
    onSuccess: syncItemsWithFilesList
  })

  useProvisionDefaultShortcuts(filesList)
  useProvisionNeedHelpShortcut(filesList)
  useProvisionNewGrantApplicationShortcut(filesList)
  useProvisionMyBillsShortcut(filesList)

  const moveFilesItemsToDesktop = useMoveFilesItemsToDesktop(cols, rows)
  const moveFilesItemsIntoFolder = useMoveFilesItemsIntoFolder()
  const moveItemsIntoFolderOnDesktop = useMoveItemsIntoFolder({
    itemsRef,
    commitManualItems
  })

  useShortcutGroupsPersistence({
    userId: user?.id,
    itemIds: items.map(item => item.id)
  })

  useProvisionOfficeToolsGroup({
    userId: user?.id,
    organizationId: user?.organization_id,
    rootFolderItems: filesList,
    items,
    cols,
    rows,
    commitManualItems
  })

  useEffect(() => {
    const groupedIds = new Set(shortcutGroups.flatMap(group => group.memberIds))
    const pinnedGroupedIds = items
      .filter(
        item =>
          item.itemType === 'shortcut' &&
          item.isPinned &&
          groupedIds.has(item.id)
      )
      .map(item => item.id)
    if (pinnedGroupedIds.length > 0) {
      removeShortcutGroupMembers(pinnedGroupedIds)
    }
  }, [items, removeShortcutGroupMembers, shortcutGroups])

  const handleShortcutGroupDrop = useCallback(
    (
      groupId: string,
      draggedItemIds: string[],
      dropPosition?: { row: number; col: number }
    ) => {
      const group = shortcutGroups.find(candidate => candidate.id === groupId)
      if (!group) return
      if (!getShortcutGroupCapabilities(group).canAcceptMembers) return

      const dragged = items.filter(
        item =>
          draggedItemIds.includes(item.id) &&
          item.itemType === 'shortcut' &&
          !item.isPinned
      )
      if (dragged.length === 0) return

      const draggedIds = new Set(dragged.map(item => item.id))
      let nextItems = items.map(item => ({
        ...item,
        position: { ...item.position }
      }))
      const placedIds: string[] = []

      dragged.forEach(item => {
        const currentGroupIds = new Set([...group.memberIds, ...placedIds])
        const currentGroupItems = nextItems.filter(
          candidate =>
            currentGroupIds.has(candidate.id) && candidate.id !== item.id
        )
        const placedDraggedIds = new Set(placedIds)
        const sourceGroupIds = shortcutGroups
          .filter(group => group.memberIds.includes(item.id))
          .map(group => group.id)
        const occupied = [
          ...nextItems.filter(
            candidate =>
              !draggedIds.has(candidate.id) ||
              placedDraggedIds.has(candidate.id)
          ),
          ...getShortcutGroupCollisionItems(shortcutGroups, nextItems, [
            groupId,
            ...sourceGroupIds
          ])
        ]
        const insertionPlan = getShortcutGroupInsertionPlan({
          members: currentGroupItems,
          item,
          occupied,
          cols,
          rows,
          dropPosition,
          reorderItemId: group.memberIds.includes(item.id)
            ? item.id
            : undefined,
          reorderItemPosition: item.position
        })
        const target = insertionPlan?.target

        // Group membership must never put an item in a disconnected or
        // arbitrary location. Keep it where it is if no adjacent cell works.
        if (!target || !insertionPlan) return

        const shiftedMemberPositions = new Map(
          insertionPlan.members.map(member => [member.id, member.position])
        )
        nextItems = nextItems.map(candidate => {
          const shiftedPosition = shiftedMemberPositions.get(candidate.id)
          if (shiftedPosition) {
            return { ...candidate, position: { ...shiftedPosition } }
          }
          return candidate.id === item.id
            ? { ...candidate, position: { ...target } }
            : candidate
        })
        placedIds.push(item.id)
      })

      commitManualItems(prevItems =>
        prevItems.map(
          item => nextItems.find(next => next.id === item.id) ?? item
        )
      )
      addMembersToShortcutGroup(groupId, placedIds)
    },
    [
      addMembersToShortcutGroup,
      cols,
      commitManualItems,
      items,
      rows,
      shortcutGroups
    ]
  )

  const handleShortcutGroupLeave = useCallback(
    (draggedItemIds: string[]) => removeShortcutGroupMembers(draggedItemIds),
    [removeShortcutGroupMembers]
  )

  const {
    handleDragStart,
    handleShortcutGroupDragStart,
    handleContainerDrop,
    handleDragOver,
    handleDragLeave,
    handleExternalPointerPreview,
    handleDragEnd,
    dropPreview,
    isExternalDragOver,
    handleTouchPointerDown,
    handleTouchPointerMove,
    handleTouchPointerEnd,
    handleTouchPointerCancel,
    isTouchReorderActive,
    isTouchEnvironment,
    computeExternalDropPositionFromPointer,
    getExternalFolderTargetAtPointer,
    hoveredFolderItemId,
    hoveredShortcutGroupId,
    shortcutGroupBoundsPreview,
    groupDropPreview,
    moveShortcutGroupBy
  } = useDragAndDrop(
    items,
    commitManualItems,
    moveFilesItemsToDesktop,
    moveFilesItemsIntoFolder,
    moveItemsIntoFolderOnDesktop,
    cellSize,
    cols,
    rows,
    offset,
    shortcutGroups,
    handleShortcutGroupDrop,
    handleShortcutGroupLeave
  )

  const marquee = useMarqueeSelection()
  const dismissContextMenus = useDismissDesktopContextMenus()
  const shortcutGroupPreview = groupDropPreview?.groupId
    ? shortcutGroups.find(group => group.id === groupDropPreview.groupId)
    : undefined
  const shortcutGroupPreviewBounds = groupDropPreview?.groupId
    ? getShortcutGroupPreviewBounds(groupDropPreview.items)
    : null

  const handleDesktopGridClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // A finished marquee owns the selection — skip the trailing-click clear.
      if (marquee.didRunRef.current) {
        marquee.didRunRef.current = false
        return
      }
      // Item clicks are stopped by the item handler, so reaching here means
      // empty-space click → clear the selection.
      if (e.target === e.currentTarget) {
        clearSelection()
      }
    },
    [clearSelection, marquee.didRunRef]
  )

  useGridResizeReflow({
    fitCols,
    fitRows,
    rows,
    itemsLength: items.length,
    shortcutGroups,
    pinnedLayoutKey,
    itemsRef,
    setItems,
    setRequiredCols,
    setRequiredRows
  })

  useDesktopItemsPersistence({ userId: user?.id, items })

  useDesktopBroadcastListener({ commitManualItems })

  const { isBridgeDragOver } = useDesktopCrossAppBridge({
    gridRef,
    refreshFilesList,
    handleExternalPointerPreview,
    computeExternalDropPositionFromPointer,
    getExternalFolderTargetAtPointer,
    moveFilesItemsToDesktop,
    moveFilesItemsIntoFolder
  })

  return (
    <DesktopGridWrapper>
      <div
        ref={gridRef}
        className={cn(styles.desktopGrid, {
          [styles.isExternalDragOver]: isExternalDragOver || isBridgeDragOver
        })}
        data-desktop-grid
        onClickCapture={dismissContextMenus}
        onClick={handleDesktopGridClick}
        onDrop={handleContainerDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        style={
          {
            gridTemplateColumns: `repeat(${Math.max(
              1,
              cols
            )}, ${cellSize.width}px)`,
            gridTemplateRows: `repeat(${Math.max(
              1,
              rows
            )}, ${cellSize.height}px)`,
            gap: `${cellSize.gapY}px ${cellSize.gapX}px`,
            '--dg-item-border-radius': `${getItemBorderRadius(
              cellSize.width
            )}px`
          } as CSSProperties
        }
      >
        <SelectionArea
          boundaries='[data-desktop-grid]'
          selectables={GRID_ITEM_SELECTOR}
          selectionAreaClass={styles.marquee}
          features={{
            singleTap: { allow: false, intersect: 'touch' },
            range: false,
            touch: false,
            deselectOnBlur: false
          }}
          behaviour={{
            intersect: 'touch',
            startThreshold: 10,
            overlap: 'keep'
          }}
          onBeforeStart={marquee.onBeforeStart}
          onStart={marquee.onStart}
          onMove={marquee.onMove}
        >
          {shortcutGroups.map(group => (
            <ShortcutGroup
              key={group.id}
              group={group}
              items={items}
              cellSize={cellSize}
              isDropTarget={hoveredShortcutGroupId === group.id}
              previewBounds={
                shortcutGroupBoundsPreview?.groupId === group.id
                  ? shortcutGroupBoundsPreview.bounds
                  : undefined
              }
              onDragStart={handleShortcutGroupDragStart}
              onDragEnd={handleDragEnd}
              onMoveByKeyboard={moveShortcutGroupBy}
            />
          ))}

          {filesList?.data
            .map(item => {
              const itemMetaData: ShortcutGridItem | undefined = items.find(
                desktopItem => desktopItem.fileId === item.id
              )
              if (!itemMetaData) return null

              return (
                <GridViewItem
                  key={item.id}
                  item={item}
                  cellSize={cellSize}
                  itemMetaData={itemMetaData}
                  isFolderDropTarget={hoveredFolderItemId === itemMetaData.id}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onPointerDown={handleTouchPointerDown}
                  onPointerMove={handleTouchPointerMove}
                  onPointerUp={handleTouchPointerEnd}
                  onPointerCancel={handleTouchPointerCancel}
                  suppressClick={isTouchReorderActive}
                  isTouchReorderActive={isTouchReorderActive}
                  disableContextMenu={isTouchEnvironment}
                />
              )
            })
            .filter(Boolean)}

          <FilePreviewModal />
          <NewFolderPlaceholder />

          {dropPreview && (
            <div
              className={styles.dropPreview}
              style={{
                gridColumnStart: dropPreview.position.col + 1,
                gridRowStart: dropPreview.position.row + 1,
                gridColumnEnd: `span ${dropPreview.size.width}`,
                gridRowEnd: `span ${dropPreview.size.height}`
              }}
            />
          )}

          {shortcutGroupPreview && shortcutGroupPreviewBounds && (
            <div
              className={cn(styles.groupDropPreview, {
                [styles.invalid]: !groupDropPreview?.isValid
              })}
              style={{
                gridColumnStart: shortcutGroupPreviewBounds.position.col + 1,
                gridRowStart: shortcutGroupPreviewBounds.position.row + 1,
                gridColumnEnd: `span ${shortcutGroupPreviewBounds.size.width}`,
                gridRowEnd: `span ${shortcutGroupPreviewBounds.size.height}`,
                // Same title-bar allowance ShortcutGroup itself reserves, so
                // the preview is always the same size as the real card.
                height: `calc(100% + ${getShortcutGroupTitleBarHeight(cellSize)}px)`,
                transform: `translateY(-${getShortcutGroupTitleBarHeight(cellSize)}px)`
              }}
            >
              <div
                className={cn(
                  groupCardStyles.title,
                  styles.groupDropPreviewTitle
                )}
              >
                {shortcutGroupPreview.title}
              </div>
            </div>
          )}

          {groupDropPreview?.items.map((preview, index) => {
            const previewItem = preview.itemId
              ? items.find(i => i.id === preview.itemId)
              : undefined
            const fileItem = previewItem
              ? filesList?.data.find(f => previewItem.fileId === f.id)
              : undefined
            return (
              <div
                key={`${preview.position.row}-${preview.position.col}-${index}`}
                className={cn(styles.dropPreview, {
                  [styles.invalid]: !groupDropPreview.isValid
                })}
                style={{
                  gridColumnStart: preview.position.col + 1,
                  gridRowStart: preview.position.row + 1,
                  gridColumnEnd: `span ${preview.size.width}`,
                  gridRowEnd: `span ${preview.size.height}`
                }}
              >
                {fileItem && (
                  <DropPreviewItem
                    item={fileItem}
                    row={preview.position.row}
                    col={preview.position.col}
                  />
                )}
              </div>
            )
          })}
        </SelectionArea>
      </div>
      <FileDropzone />
      <FileUploadProgressToast />
    </DesktopGridWrapper>
  )
}

export const DesktopGrid: FC<DesktopGridProps> = props => (
  <DesktopGridContent {...props} />
)
